import React, { CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import AuditModuleService, {
  AuditExportOptions,
  AuditExportParams,
  AuditExportPreview,
} from "src/services/auditModuleService";
import { downloadFile } from "src/utils/fileUtil";
import { defaultQueueRange } from "../shared/helpers";
import * as S from "../shared/styles";

// ----------------------------  Export Builder  ----------------------------
//
// Export & Reports > Export Builder: downloads submitted reviews joined with their
// sample records, one row per (record, reviewer). Drafts are never exported. The
// backend scopes the use cases (Admins: all, Program Owners and Auditors: the ones
// granted to them), builds the columns and caps the CSV at max_rows (newest
// inspections first). The date range is the Safety Genie inspection completed date.

// wait this long after the last keystroke before searching by record ID
export const SEARCH_DEBOUNCE_MS = 400;

export const STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "PartiallyReviewed", label: "Partially reviewed" },
  { value: "FullyReviewed", label: "Fully reviewed" },
];

// "" = All in every dropdown
export interface ExportFilters {
  useCaseId: string;
  reviewer: string;
  site: string;
  start: string;
  end: string;
  status: string;
  // record ID contains; "" = any
  search: string;
}

/** Query params for the export endpoints; "All" / empty filters are left out. */
export const toExportParams = (f: ExportFilters): AuditExportParams => {
  const params: AuditExportParams = {};
  if (f.useCaseId) params.use_case_ids = f.useCaseId;
  if (f.reviewer) params.reviewers = f.reviewer;
  if (f.site) params.sites = f.site;
  if (f.start) params.start_date = f.start;
  if (f.end) params.end_date = f.end;
  if (f.status) params.status = f.status;
  const search = f.search.trim();
  if (search) params.search = search;
  return params;
};

/** Default filters: everything in scope, inspections completed in the last 2 months. */
export const defaultExportFilters = (today: Date = new Date()): ExportFilters => ({
  useCaseId: "",
  reviewer: "",
  site: "",
  ...defaultQueueRange(today),
  status: "",
  search: "",
});

const ExportBuilder: React.FC = () => {
  const [options, setOptions] = useState<AuditExportOptions | null>(null);
  const [optionsFailed, setOptionsFailed] = useState(false);
  const [filters, setFilters] = useState<ExportFilters>(() => defaultExportFilters());
  const [preview, setPreview] = useState<AuditExportPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);
  // typed record ID; copied into filters.search once typing pauses
  const [searchInput, setSearchInput] = useState("");
  // only the latest preview request may update the page
  const previewRequest = useRef(0);

  const params = useMemo(() => toExportParams(filters), [filters]);
  const update = (patch: Partial<ExportFilters>) => setFilters((f) => ({ ...f, ...patch }));

  useEffect(() => {
    // unchanged -> same filters object, so no extra preview request
    const timer = setTimeout(
      () => setFilters((f) => (f.search === searchInput ? f : { ...f, search: searchInput })),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    AuditModuleService.getExportOptions()
      // a missing list (e.g. an older backend without sites) is treated as empty
      .then((data) =>
        setOptions({
          use_cases: data?.use_cases ?? [],
          reviewers: data?.reviewers ?? [],
          sites: data?.sites ?? [],
        }),
      )
      .catch(() => setOptionsFailed(true));
  }, []);

  useEffect(() => {
    const id = ++previewRequest.current;
    setPreviewLoading(true);
    setPreviewFailed(false);
    AuditModuleService.getExportPreview(params)
      .then((data) => {
        if (id === previewRequest.current) setPreview(data);
      })
      .catch(() => {
        if (id === previewRequest.current) setPreviewFailed(true);
      })
      .finally(() => {
        if (id === previewRequest.current) setPreviewLoading(false);
      });
  }, [params]);

  const download = async () => {
    setDownloading(true);
    setDownloadFailed(false);
    try {
      const blob = await AuditModuleService.downloadExportCsv(params);
      const url = window.URL.createObjectURL(blob);
      downloadFile(url, `audit-export-${new Date().toISOString().slice(0, 10)}.csv`);
      // revoke after the click has started the download (an immediate revoke can abort it in Firefox)
      setTimeout(() => window.URL.revokeObjectURL(url), 0);
    } catch {
      setDownloadFailed(true);
    } finally {
      setDownloading(false);
    }
  };

  const count = preview?.count ?? 0;
  const maxRows = preview?.max_rows ?? 0;
  const csvRows = preview?.truncated ? maxRows : count;
  const canDownload = !previewLoading && !previewFailed && count > 0 && !downloading;

  return (
    <div style={S.pageWrapper}>
      <div style={{ marginBottom: "20px" }}>
        <h1 style={S.pageTitle}>Export &amp; Reports</h1>
        <p style={S.pageSubtitle}>
          Download submitted review results joined with their records, one row per reviewer.
        </p>
      </div>

      <div style={{ ...S.tabGroup, marginBottom: "24px" }} role="tablist">
        <button type="button" role="tab" aria-selected style={S.tabActive}>
          Export Builder
        </button>
      </div>

      {/* Filters */}
      <div style={{ ...S.configCard, marginBottom: "16px" }}>
        <div style={filterGrid}>
          <div>
            <div style={groupLabel}>Record ID</div>
            <input
              type="search"
              aria-label="Record ID"
              placeholder="Contains..."
              style={S.filterSelect}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
          <div>
            <div style={groupLabel}>Use case</div>
            <select
              aria-label="Use case"
              style={S.filterSelect}
              value={filters.useCaseId}
              onChange={(e) => update({ useCaseId: e.target.value })}
            >
              <option value="">All</option>
              {options?.use_cases.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div style={groupLabel}>Reviewer</div>
            <select
              aria-label="Reviewer"
              style={S.filterSelect}
              value={filters.reviewer}
              onChange={(e) => update({ reviewer: e.target.value })}
            >
              <option value="">All</option>
              {options?.reviewers.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div style={groupLabel}>Site</div>
            <select
              aria-label="Site"
              style={S.filterSelect}
              value={filters.site}
              onChange={(e) => update({ site: e.target.value })}
            >
              <option value="">All</option>
              {options?.sites.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div style={groupLabel}>Record status</div>
            <select
              aria-label="Record status"
              style={S.filterSelect}
              value={filters.status}
              onChange={(e) => update({ status: e.target.value })}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div
              style={groupLabel}
              title="Date the inspection was completed in Safety Genie, UTC days"
            >
              Date
            </div>
            <div style={S.filterRow}>
              <input
                type="date"
                aria-label="From"
                style={S.filterSelect}
                value={filters.start}
                max={filters.end || undefined}
                onChange={(e) => update({ start: e.target.value })}
              />
              <span style={muted}>→</span>
              <input
                type="date"
                aria-label="To"
                style={S.filterSelect}
                value={filters.end}
                min={filters.start || undefined}
                onChange={(e) => update({ end: e.target.value })}
              />
            </div>
          </div>
          <button
            type="button"
            style={{
              ...S.btnPrimary,
              marginLeft: "auto",
              alignSelf: "flex-end",
              ...(canDownload ? {} : disabledBtn),
            }}
            disabled={!canDownload}
            onClick={download}
          >
            {downloading ? "Downloading…" : `Download CSV (${csvRows.toLocaleString()} rows)`}
          </button>
        </div>
        {optionsFailed && (
          <div style={errorText} role="alert">
            Couldn't load the use case, reviewer and site lists. Please refresh.
          </div>
        )}
        {preview?.truncated && (
          <div style={warning} role="status">
            {count.toLocaleString()} rows match, but the CSV holds only the newest{" "}
            {maxRows.toLocaleString()}. Narrow the filters to export the rest.
          </div>
        )}
        {downloadFailed && (
          <div style={errorText} role="alert">
            Download failed. Please try again.
          </div>
        )}
      </div>

      {/* Preview */}
      <div style={S.configCard}>
        <div style={previewHeader}>
          <span style={previewTitle}>Preview</span>
          {preview && !previewFailed && (
            <span style={muted}>
              first {preview.rows.length} of {count.toLocaleString()} rows ·{" "}
              {preview.headers.length} columns
            </span>
          )}
        </div>
        {previewFailed ? (
          <p style={errorText} role="alert">
            Couldn't load the preview. Please try again.
          </p>
        ) : !preview ? (
          <p style={emptyText}>Loading…</p>
        ) : count === 0 ? (
          <p style={emptyText}>No submitted reviews match these filters.</p>
        ) : (
          <div style={{ ...tableWrap, opacity: previewLoading ? 0.5 : 1 }}>
            <table style={previewTable}>
              <thead>
                <tr>
                  {preview.headers.map((h) => (
                    <th key={h} style={previewTh}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((row, i) => (
                  <tr key={i}>
                    {preview.headers.map((h) => (
                      <td key={h} style={previewTd} title={row[h]}>
                        {row[h]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExportBuilder;

// ----------------------------  Styles  ----------------------------

const filterGrid: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "flex-start",
  gap: "24px",
};

const groupLabel: CSSProperties = {
  fontSize: "0.8rem",
  color: "#6b7280",
  marginBottom: "6px",
};

const muted: CSSProperties = { fontSize: "0.85rem", color: "#9ca3af" };

const disabledBtn: CSSProperties = { opacity: 0.5, cursor: "not-allowed" };

const warning: CSSProperties = {
  marginTop: "12px",
  padding: "8px 12px",
  borderRadius: "6px",
  background: "#fef3c7",
  color: "#92400e",
  fontSize: "0.875rem",
};

const errorText: CSSProperties = { marginTop: "12px", color: "#ef4444", fontSize: "0.875rem" };

const previewHeader: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  marginBottom: "12px",
};

const previewTitle: CSSProperties = { fontWeight: 600, color: "#111827" };

const emptyText: CSSProperties = { ...muted, textAlign: "center", padding: "24px 0" };

const tableWrap: CSSProperties = {
  overflow: "auto",
  maxHeight: "55vh",
  border: "1px solid #f3f4f6",
  borderRadius: "6px",
};

const previewTable: CSSProperties = {
  borderCollapse: "collapse",
  fontSize: "0.8rem",
  whiteSpace: "nowrap",
};

const previewTh: CSSProperties = {
  position: "sticky",
  top: 0,
  background: "#f9fafb",
  textAlign: "left",
  fontWeight: 500,
  color: "#6b7280",
  padding: "8px",
  borderBottom: "1px solid #e5e7eb",
};

const previewTd: CSSProperties = {
  padding: "6px 8px",
  color: "#4b5563",
  borderBottom: "1px solid #f3f4f6",
  maxWidth: "220px",
  overflow: "hidden",
  textOverflow: "ellipsis",
};
